const clientNameToBadge: Record<string, string> = {
  CJ대한통운: "CJ",
  한진택배: "한진",
  롯데택배: "롯데",
}

/**
 * forwarding_info 기반 배지 표기 매핑
 * 기준: 네이버에서 어떤 택배사로 송장번호 검색해야 하는지
 * key: `${brokerOrganizationName}:${deliveryOrganizationName}`
 */
const forwardingBadgeMap: Record<string, string> = {
  "WITHU:CJ": "CJ",
  "WITHU:HANJIN": "한진",
  "HANJIN:HANJIN": "한진",
  "HANJIN:TO_DAY": "한진",
  ":EMS": "EMS",
}

const shippingIdPreferredClientIdsByEnv: Record<string, string[]> = {
  /**
   * FIELD_ID | PROD_ID | NAME | COMPANY_NAME
   * 525887971183 | 110201815933 | CJ대한통운 | CJ대한통운(주)
   * 585211958273 | 122741676407 | 롯데택배 | 롯데택배
   *            - | 164880727013 | 한진택배 | (주)한진
   *            - | 107054273515 | 알리익스프레스 | 알리익스프레스
   * 535690403893 | 170067347259 | TY | TY
   * 593588772084 | 139710717700 | 타오바오 | 타오바오
   * 544503821229 | 155570418612 | 우진 | 우진 (직연동)
   */
  field: [
    "525887971183",
    "585211958273",
    "535690403893",
    "593588772084",
    "544503821229",
  ],
  prod: [
    "110201815933",
    "122741676407",
    "164880727013",
    "107054273515",
    "170067347259",
    "139710717700",
    "155570418612",
  ],
}

export function getShippingIdPreferredClientIds(env?: string): string[] {
  if (env && env in shippingIdPreferredClientIdsByEnv) {
    return shippingIdPreferredClientIdsByEnv[env]
  }
  return shippingIdPreferredClientIdsByEnv["field"]
}

export type LoadDisplayInfo = {
  invoiceNumber: string
  organization?: string
}

export type LoadForDisplayInvoiceNumber = {
  forwardingInfo?: {
    invoiceNumber: string
    brokerOrganizationName?: string
    deliveryOrganizationName: string
  } | null
  clientId?: string | null
  clientShippingId?: string | null
  invoiceNumber: string
  clientName: string
  sender?: { name?: string | null } | null
}

function getForwardingBadge(
  brokerOrganizationName: string | undefined,
  deliveryOrganizationName: string
): string | undefined {
  const key = `${brokerOrganizationName ?? ""}:${deliveryOrganizationName}`
  return forwardingBadgeMap[key]
}

function getClientBadge(clientName: string): string | undefined {
  return clientNameToBadge[clientName]
}

/**
 * 화면에 표시할 송장번호를 결정합니다.
 *
 * 우선순위:
 * 1. forwardingInfo.invoiceNumber (위탁택배 송장)
 * 2. clientShippingId (clientId가 shippingIdPreferredClientIds에 포함된 고객사)
 * 3. 우진 + 판토스 특수케이스
 * 4. invoiceNumber (기본값)
 */
export function getLoadDisplayInvoiceNumber(
  load: LoadForDisplayInvoiceNumber,
  shippingIdPreferredClientIds: string[]
): LoadDisplayInfo {
  if (load.forwardingInfo?.invoiceNumber) {
    const forwardingBadge = getForwardingBadge(
      load.forwardingInfo.brokerOrganizationName,
      load.forwardingInfo.deliveryOrganizationName
    )
    const badge = forwardingBadge ?? getClientBadge(load.clientName)
    return {
      invoiceNumber: load.forwardingInfo.invoiceNumber,
      ...(badge && { organization: badge }),
    }
  }

  if (load.clientId && shippingIdPreferredClientIds.includes(load.clientId)) {
    const badge = getClientBadge(load.clientName)
    return {
      invoiceNumber: load.clientShippingId || load.invoiceNumber,
      ...(badge && { organization: badge }),
    }
  }

  // 익일택배 우진 물품은 실제로 판토스 송장번호이다. 우진 물품의 sender_name = 판토스로 접수된다.
  if (load.clientName === "우진" && load.sender?.name === "판토스") {
    return {
      invoiceNumber: load.clientShippingId || load.invoiceNumber,
      organization: "판토스",
    }
  }

  return {
    invoiceNumber: load.invoiceNumber,
  }
}
